<script setup>
import { getDetailStockMovementBySKU } from '@/api/inventory'
import { getWarehouseById } from '@/api/warehouse'
import DateIcon from '@/components/Icons/DateV2.vue'
import { i18n } from '@/i18n'
import { useAsyncState, useDebounceFn } from '@vueuse/core'
import moment from 'moment'
import { computed, getCurrentInstance, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router/composables'
import ModalExportExcel from './ModalExportExcel.vue'

const showOriginalValue = computed(() => false)

const MOVEMENT_STATUS_MAPPER = {
  INBOUND: { availability: 'arrow-up', on_order: '=', on_hand: 'arrow-up', reserved_stock: '=' },
  BOOKING: { availability: 'arrow-down', on_order: 'arrow-up', on_hand: '=', reserved_stock: '=' },
  BOOKING_RELEASE: { availability: 'arrow-up', on_order: 'arrow-down', on_hand: '=', reserved_stock: '=' },
  INBOUND_CANCEL: { availability: 'arrow-down', on_order: '=', on_hand: 'arrow-down', reserved_stock: '=' },
  OUTBOUND: { availability: '=', on_order: 'arrow-down', on_hand: 'arrow-down', reserved_stock: '=' },
  OUTBOUND_RELEASE: { availability: '=', on_order: 'arrow-up', on_hand: 'arrow-up', reserved_stock: '=' },
  ADJIN: { availability: 'arrow-up', on_order: '=', on_hand: 'arrow-up', reserved_stock: '=' },
  ADJOUT: { availability: 'arrow-down', on_order: '=', on_hand: 'arrow-down', reserved_stock: '=' },
  ADJ_BOOKING: { availability: 'arrow-down', on_order: 'arrow-up', on_hand: '=', reserved_stock: '=' },
  INIT: { availability: 'arrow-down', on_order: '=', on_hand: 'arrow-down', reserved_stock: '=' },
  TRANSFERIN: { availability: 'arrow-up', on_order: '=', on_hand: 'arrow-up', reserved_stock: '=' },
  TRANSFEROUT: { availability: 'arrow-down', on_order: '=', on_hand: 'arrow-down', reserved_stock: '=' },
  FGINBOUND: { availability: 'arrow-down', on_order: '=', on_hand: 'arrow-down', reserved_stock: '=' },
  FGCOMPOUTBOUND: { availability: 'arrow-down', on_order: '=', on_hand: 'arrow-down', reserved_stock: '=' },
  RETURN: { availability: 'arrow-up', on_order: '=', on_hand: 'arrow-up', reserved_stock: '=' },
  RETURN_RELEASE: { availability: 'arrow-down', on_order: '=', on_hand: 'arrow-down', reserved_stock: '=' },
  TRANSFEROUT_RELEASE: { availability: 'arrow-up', on_order: '=', on_hand: 'arrow-up', reserved_stock: '=' },
  RESERVED: { availability: 'arrow-up', on_order: '=', on_hand: '=', reserved_stock: 'arrow-down' },
}

const route = useRoute()
const router = useRouter()
const { $notification } = getCurrentInstance().proxy

const catalogId = computed(() => route.params.id)
const warehouseId = computed(() => route.query.warehouse_id)

const VISIBLE_MOVEMENT_TYPES = [
  'INBOUND', 'INBOUND_CANCEL', 'OUTBOUND', 'OUTBOUND_RELEASE', 'ADJIN', 'ADJOUT',
  'ADJ_BOOKING', 'BOOKING', 'BOOKING_RELEASE', 'TRANSFERIN', 'TRANSFEROUT',
  'TRANSFEROUT_RELEASE', 'TSTIN', 'TSTOUT', 'PTPIN', 'PTPOUT', 'FGINBOUND',
  'FGCOMPOUTBOUND', 'RETURN', 'RETURN_RELEASE', 'RESERVED',
]

const HIDDEN_MOVEMENT_TYPES = [
  'SYNC_RECALC',
]

const params = ref({
  reff_text: route.query.reff_text || '',
  movement_type: route.query.movement_type ? route.query.movement_type.split(',') : ['ALL'],
  sort: route.query.sort || 'movementDate,Desc',
  page: Number(route.query.page) || 1,
  size: Number(route.query.size) || 10,
})

const startDate = ref(route.query.periode_awal ? moment(route.query.periode_awal) : moment().startOf('day'))
const endDate = ref(route.query.periode_akhir ? moment(route.query.periode_akhir) : moment().endOf('day'))

const movementStatusList = computed(() => [
  { label: i18n.t('productAvailability.movement_status.all'), value: "ALL" },
  { label: i18n.t('productAvailability.movement_status.good_receive'), value: "INBOUND" },
  { label: i18n.t('productAvailability.movement_status.cancel_good_receive'), value: "INBOUND_CANCEL" },
  { label: i18n.t('productAvailability.movement_status.good_issued'), value: "OUTBOUND" },
  { label: i18n.t('productAvailability.movement_status.cancel_good_issued'), value: "OUTBOUND_RELEASE" },
  { label: i18n.t('productAvailability.movement_status.adj_in'), value: "ADJIN" },
  { label: i18n.t('productAvailability.movement_status.adj_out'), value: "ADJOUT" },
  { label: i18n.t('productAvailability.movement_status.adj_booking'), value: "ADJ_BOOKING" },
  { label: i18n.t('productAvailability.movement_status.booking_order'), value: "BOOKING" },
  { label: i18n.t('productAvailability.movement_status.release_booking'), value: "BOOKING_RELEASE" },
  { label: i18n.t('productAvailability.movement_status.transfer_in'), value: "TRANSFERIN" },
  { label: i18n.t('productAvailability.movement_status.transfer_out'), value: "TRANSFEROUT" },
  { label: i18n.t('productAvailability.movement_status.cancel_transfer_out'), value: "TRANSFEROUT_RELEASE" },
  { label: i18n.t('productAvailability.movement_status.type_transfer_in'), value: "TSTIN" },
  { label: i18n.t('productAvailability.movement_status.type_transfer_out'), value: "TSTOUT" },
  { label: i18n.t('productAvailability.movement_status.ptp_in'), value: "PTPIN" },
  { label: i18n.t('productAvailability.movement_status.ptp_out'), value: "PTPOUT" },
  { label: i18n.t('productAvailability.movement_status.vas_inbound'), value: "FGINBOUND" },
  { label: i18n.t('productAvailability.movement_status.vas_outbound'), value: "FGCOMPOUTBOUND" },
  { label: i18n.t('productAvailability.movement_status.return'), value: "RETURN" },
  { label: i18n.t('productAvailability.movement_status.cancel_return'), value: "RETURN_RELEASE" },
  { label: i18n.t('productAvailability.movement_status.reserved'), value: "RESERVED" },
])

const detailWarehouse = ref(null)
const fetchWarehouse = async () => {
  if (!warehouseId.value) return
  try {
    const { data } = await getWarehouseById(warehouseId.value)
    detailWarehouse.value = data.data
  } catch (err) {
    $notification.error({ message: err?.response?.data?.message || 'Warehouse not found' })
  }
}

const { state: movementData, isLoading, execute: fetchMovement } = useAsyncState(async () => {
  let mTypes = [...params.value.movement_type]
  
  if (mTypes.includes('ALL')) {
    // If ALL is selected, send everything (Visible + Hidden)
    mTypes = [...VISIBLE_MOVEMENT_TYPES, ...HIDDEN_MOVEMENT_TYPES]
  }
  
  const payload = {
    warehouse_id: warehouseId.value,
    catalog_id: catalogId.value,
    ...params.value,
    movement_type: mTypes.join(','),
    periode_awal: startDate.value ? startDate.value.startOf('day').format() : undefined,
    periode_akhir: endDate.value ? endDate.value.endOf('day').format() : undefined,
  }

  const { data } = await getDetailStockMovementBySKU(payload)
  return data
}, { data: [], total_row: 0 }, { immediate: false, resetOnExecute: false })

const listStockMovement = computed(() => movementData.value?.data || [])
const totalRow = computed(() => movementData.value?.total_row || 0)



const ranges = {
  [i18n.t('productAvailability.ranges.today')]: [moment(), moment()],
  [i18n.t('productAvailability.ranges.this_month')]: [moment().startOf('month'), moment().endOf('month')],
  [i18n.t('productAvailability.ranges.this_year')]: [moment().startOf('year'), moment().endOf('year')],
  [i18n.t('productAvailability.ranges.last_7_days')]: [moment().subtract(7, 'days'), moment()],
  [i18n.t('productAvailability.ranges.last_30_days')]: [moment().subtract(1, 'month'), moment()],
  [i18n.t('productAvailability.ranges.last_year')]: [moment().subtract(1, 'year'), moment()],
}

watch(() => params.value.movement_type, (newVal) => {
  if (!newVal || newVal.length === 0) {
    params.value.movement_type = ['ALL']
  }
})

const onSelectStateMovement = (val) => {
  if (val === 'ALL') {
    params.value.movement_type = ['ALL']
  } else {
    // If ALL was selected and a specific item is selected, remove ALL and switch to that item
    if (params.value.movement_type.includes('ALL')) {
      params.value.movement_type = [val]
    } else {
      // Smart Select All: if all visible items are selected manually, collapse to 'ALL'
      const currentSelected = params.value.movement_type.filter(t => t !== 'ALL')
      const isAllVisibleSelected = VISIBLE_MOVEMENT_TYPES.every(t => currentSelected.includes(t))
      
      if (isAllVisibleSelected) {
        params.value.movement_type = ['ALL']
      }
    }
  }
}

const onDeselectStateMovement = (val) => {
  if (val === 'ALL') {
    params.value.movement_type = []
  } else if (params.value.movement_type.includes('ALL')) {
    // If ALL was active but one item is deselected, expand to remaining visible items
    params.value.movement_type = VISIBLE_MOVEMENT_TYPES.filter(t => t !== val)
  }
}

const onChangeDate = (dates) => {
  startDate.value = dates[0] || null
  endDate.value = dates[1] || null
}

const updateQuery = () => {
  router.replace({
    query: {
      ...route.query,
      ...params.value,
      movement_type: params.value.movement_type.join(','),
      periode_awal: startDate.value ? startDate.value.format() : undefined,
      periode_akhir: endDate.value ? endDate.value.format() : undefined,
    },
  }).catch(() => {})
}

const filterDetailStockMovement = () => {
  params.value.page = 1
  updateQuery()
  fetchMovement()
}

const onPageChange = (p) => {
  params.value.page = p
  updateQuery()
  fetchMovement()
}

const onShowSizeChange = (c, s) => {
  params.value.size = s
  params.value.page = 1
  updateQuery()
  fetchMovement()
}

const getMovementTypeLabel = (type) => {
  return movementStatusList.value.find(i => i.value === type)?.label || type
}

const getMovementIcon = (type, field, quantity) => {
  const iconConfig = MOVEMENT_STATUS_MAPPER[type]?.[field]
  if (!iconConfig || iconConfig === '=') return null

  const q = Number(quantity)
  if (isNaN(q) || q === 0) return iconConfig

  // If quantity is negative and type is RESERVED, flip the direction from the mapper
  if (q < 0 && type === 'RESERVED') {
    return iconConfig === 'arrow-up' ? 'arrow-down' : 'arrow-up'
  }
  
  return iconConfig
}

const toggleSort = () => {
  const currentSort = params.value.sort
  if (currentSort === 'movementDate,Desc') {
    params.value.sort = 'movementDate,Asc'
  } else {
    params.value.sort = 'movementDate,Desc'
  }
  updateQuery()
  fetchMovement()
}

const checkColorArrow = (icon) => icon === 'arrow-up' ? '#07beb8cc' : '#ff0a54cc'

const showModalExportExcel = ref(false)
const loadingExport = ref(false)
const titleDetailStockMovement = computed(() => {
  const sku = route.query.product_sku || 'Data'
  const date = moment().format('YYYYMMDD_HHmmss')
  return `Stock_Movement_${sku}_${date}`
})

const exportDataTable = async (data) => {
  loadingExport.value = true
  try {
    let mTypes = data.selectedMovementType
    if (mTypes.includes('ALL')) {
      mTypes = [...VISIBLE_MOVEMENT_TYPES, ...HIDDEN_MOVEMENT_TYPES].join(',')
    }

    const payload = {
      warehouse_id: warehouseId.value,
      catalog_id: catalogId.value,
      page: 1,
      size: data.totalData,
      periode_awal: moment(data.startDate).startOf('day').format(),
      periode_akhir: moment(data.endDate).endOf('day').format(),
      movement_type: mTypes,
      sort: 'movementDate,Desc',
    }
    const { data: res } = await getDetailStockMovementBySKU(payload)
    
    import('xlsx').then((XLSX) => {
       const flattenData = res.data.map((obj) => ({
          date: moment(obj.movement_date).format('DD MMM YYYY, HH:mm'),
          movement_type: obj.movement_type,
          movement_status: getMovementTypeLabel(obj.movement_type),
          reff: obj.reff_text,
          movement_qty: Math.abs(obj.movement_quantity),
          on_hand: obj.on_hand,
          reserved_stock: obj.reserved_stock,
          on_order: obj.on_order,
          availability: obj.availability,
        }))
        const rowData = [
          i18n.t('productAvailability.updatedAt'),
          i18n.t('productAvailability.transaction'),
          i18n.t('productAvailability.movement_type_label'),
          'Reff',
          i18n.t('productAvailability.movement_quantity'),
          i18n.t('productAvailability.onHand'),
          i18n.t('productAvailability.reserved_stock'),
          i18n.t('productAvailability.inorder'),
          i18n.t('productAvailability.available'),
        ]
        const worksheet = XLSX.utils.json_to_sheet(flattenData);
        const workbook = XLSX.utils.book_new()
        XLSX.utils.sheet_add_aoa(worksheet, [rowData], { origin: "A1" });
        XLSX.utils.book_append_sheet(workbook, worksheet, 'data')
        XLSX.writeFile(workbook,`${titleDetailStockMovement.value}.xlsx`)
        loadingExport.value = false
        showModalExportExcel.value = false
    }).catch(err => {
      console.error('XLSX Load Error:', err)
      loadingExport.value = false
    })
  } catch (err) {
    loadingExport.value = false
    console.error('Export API Error:', err)
  }
}

onMounted(() => {
  fetchWarehouse()
  fetchMovement()
})

const filterOption = (input, option) => {
  return option.componentOptions.children[0].text.toLowerCase().includes(input.toLowerCase())
}
function copyToClipboard(text) {
  if (!text) return
  navigator.clipboard.writeText(text).then(() => {
    $notification.success({
      message: i18n.t('productAvailability.copy_success', { value: text }) || `Copied ${text}`,
    })
  })
}
</script>

<template>
  <div class="page__movement-detail">
    <div class="header-detail mb-4">
      <div class="header-detail__container">
        <h1 class="header-detail__title h3">
          <a-button type="link" size="small" class="mr-2" @click="router.back()">
            <a-icon type="left" />
          </a-button>
          {{ $t('productAvailability.details') }}
        </h1>
        <div class="header-detail__divider" />
        <div class="sku-copy-wrapper" @click="copyToClipboard(route.query.product_sku)">
          <span class="sku-label">SKU</span>
          <span class="sku-value">{{ route.query.product_sku }}</span>
          <a-icon type="copy" class="copy-icon" />
        </div>
        <div class="header-detail__product" :title="route.query.product_title">
          {{ route.query.product_title }}
        </div>
        <a-tag v-if="detailWarehouse?.name" class="warehouse-pill">
          <a-icon type="environment" class="mr-1" />
          {{ detailWarehouse.name }}
        </a-tag>
      </div>
    </div>

    <a-row :gutter="16" type="flex" align="middle">
      <a-col :span="6">
        <a-select 
          v-model="params.movement_type" 
          allow-clear
          mode="multiple"
          size="large"
          class="w-100 select-antd-default"
          show-search
          option-filter-prop="children"
          :max-tag-count="1"
          :placeholder="$t('productAvailability.movement_type_select')"
          :disabled="isLoading"
          @select="onSelectStateMovement"
          @deselect="onDeselectStateMovement"
        >
          <a-select-option v-for="item in movementStatusList" :key="item.value" :value="item.value">
            {{ item.label }}
          </a-select-option>
        </a-select>
      </a-col>
      <a-col :span="4">
        <a-input
          v-model="params.reff_text"
          size="large"
          class="w-100"
          allow-clear
          :placeholder="$t('productAvailability.enter_reff')"
        />
      </a-col>
      <a-col :span="6">
        <a-range-picker
          size="large"
          class="w-100"
          :ranges="ranges"
          format="DD MMMM YYYY"
          :value="[startDate, endDate]"
          :disabled="isLoading"
          allow-clear
          @change="onChangeDate"
        >
          <DateIcon slot="suffixIcon" style="color: #999999" />
        </a-range-picker>
      </a-col>
      <a-col>
        <a-button
          type="primary"
          size="large"
          :loading="isLoading"
          @click.prevent="filterDetailStockMovement"
        >
          <a-icon type="search" />
          {{ $t('utils.filter') }}
        </a-button>
      </a-col>
      <a-col class="ml-auto">
        <a-button
          type="primary"
          size="large"
          @click.prevent="showModalExportExcel = true"
        >
          <a-icon type="download" />
          {{ $t('warehouse.export_data') }}
        </a-button>
      </a-col>
    </a-row>

    <a-tabs size="large" class="pca-tab-store mt-4" :active-key="1">
      <a-tab-pane :key="1" :tab="$t('productAvailability.stock_movement')" />
    </a-tabs>

    <div class="inventory-list" :class="{ 'is-loading': isLoading }">
      <div v-if="isLoading" class="loader">
        <a-spin />
      </div>

      <div class="inventory-list--wrapper" :style="{ minHeight: listStockMovement.length ? '500px' : 'auto' }">
        <table v-if="listStockMovement.length">
          <colgroup>
            <col style="width: 180px; min-width: 180px;" />
            <col />
            <col style="width: 150px; min-width: 150px;" />
            <col style="width: 150px; min-width: 150px;" />
            <col style="width: 150px; min-width: 150px;" />
            <col style="width: 150px; min-width: 150px;" />
            <col style="width: 150px; min-width: 150px;" />
          </colgroup>
          <thead>
            <tr>
              <th rowspan="2" class="cursor-pointer" @click="toggleSort">
                <div class="d-flex align-items-center">
                  {{ $t('productAvailability.updatedAt') }}
                  <div class="ml-2 d-flex flex-column" style="font-size: 8px; line-height: 1;">
                    <a-icon type="caret-up" :style="{ color: params.sort === 'movementDate,Asc' ? '#1890ff' : '#bfbfbf' }" />
                    <a-icon type="caret-down" :style="{ color: params.sort === 'movementDate,Desc' ? '#1890ff' : '#bfbfbf' }" />
                  </div>
                </div>
              </th>
              <th rowspan="2">
                {{ $t('productAvailability.transaction') }}
              </th>
              <th rowspan="2" class="text-right" style="padding-right: 24px;">
                {{ $t('productAvailability.movementQty') }}
              </th>
              <th colspan="4" class="text-center" style="padding: 10px 12px; background-color: #e8e8e8; vertical-align: middle;">
                {{ $t('productAvailability.stock') }}
              </th>
            </tr>
            <tr class="stock">
              <th class="header-stock" style="padding: 12px 8px !important; background: #f8f9fa;">
                <div class="stock-cell-wrapper">
                  <span style="text-transform: uppercase;">{{ $t('productAvailability.onHand') }}</span>
                  <div class="stock-icon-area">
                    <a-tooltip placement="topRight" :title="$t('productAvailability.on_hand_tooltip')">
                      <a-icon type="info-circle" style="font-size: 12px; color: #bfbfbf;" />
                    </a-tooltip>
                  </div>
                </div>
              </th>
              <th class="header-stock" style="padding: 12px 8px !important; background: #f8f9fa;">
                <div class="stock-cell-wrapper">
                  <span style="text-transform: uppercase;">{{ $t('productAvailability.reserved_stock') }}</span>
                  <div class="stock-icon-area">
                    <a-tooltip :title="$t('productAvailability.reserved_stock_tooltip')">
                      <a-icon type="info-circle" style="font-size: 12px; color: #bfbfbf;" />
                    </a-tooltip>
                  </div>
                </div>
              </th>
              <th class="header-stock" style="padding: 12px 8px !important; background: #f8f9fa;">
                <div class="stock-cell-wrapper">
                  <span style="text-transform: uppercase;">{{ $t('productAvailability.inorder') }}</span>
                  <div class="stock-icon-area">
                    <a-tooltip :title="$t('productAvailability.on_order_tooltip')">
                      <a-icon type="info-circle" style="font-size: 12px; color: #bfbfbf;" />
                    </a-tooltip>
                  </div>
                </div>
              </th>
              <th class="header-stock" style="padding: 12px 8px !important; background: #f8f9fa;">
                <div class="stock-cell-wrapper">
                  <span style="text-transform: uppercase;">{{ $t('productAvailability.available') }}</span>
                  <div class="stock-icon-area">
                    <a-tooltip :title="$t('productAvailability.sellable_stock_tooltip')">
                      <a-icon type="info-circle" style="font-size: 12px; color: #bfbfbf;" />
                    </a-tooltip>
                  </div>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="record in listStockMovement" :key="record.id">
              <td style="white-space: nowrap;">
                {{ moment(record.movement_date).format('DD MMM YYYY, HH:mm:ss') }}
              </td>
              <td style="word-break: break-all;">
                <div>{{ getMovementTypeLabel(record.movement_type) }}</div>
                <div v-if="record.reff_text" class="text-muted small">
                  {{ record.reff_text }}
                </div>
              </td>
              <td class="text-right movement-quantity" style="padding-right: 24px;">
                {{ Math.abs(record.movement_quantity) }}
              </td>
              <td class="inventory-list-item__stock">
                <div class="stock-cell-wrapper stock-on-hand">
                  <span>{{ record.on_hand ?? 0 }}</span>
                  <div class="stock-icon-area">
                    <a-icon
                      v-if="getMovementIcon(record.movement_type, 'on_hand', record.movement_quantity)" 
                      :type="getMovementIcon(record.movement_type, 'on_hand', record.movement_quantity)" 
                      :style="{ color: checkColorArrow(getMovementIcon(record.movement_type, 'on_hand', record.movement_quantity)) }"
                    />
                  </div>
                </div>
              </td>
              <td class="inventory-list-item__stock">
                <div class="stock-cell-wrapper stock-reserved">
                  <span>{{ record.reserved_stock ?? 0 }}</span>
                  <div class="stock-icon-area">
                    <a-icon
                      v-if="getMovementIcon(record.movement_type, 'reserved_stock', record.movement_quantity)" 
                      :type="getMovementIcon(record.movement_type, 'reserved_stock', record.movement_quantity)" 
                      :style="{ color: checkColorArrow(getMovementIcon(record.movement_type, 'reserved_stock', record.movement_quantity)) }"
                    />
                  </div>
                </div>
              </td>
              <td class="inventory-list-item__stock">
                <div class="stock-cell-wrapper stock-on-order">
                  <span>{{ record.on_order ?? 0 }}</span>
                  <div class="stock-icon-area">
                    <a-icon
                      v-if="getMovementIcon(record.movement_type, 'on_order', record.movement_quantity)" 
                      :type="getMovementIcon(record.movement_type, 'on_order', record.movement_quantity)" 
                      :style="{ color: checkColorArrow(getMovementIcon(record.movement_type, 'on_order', record.movement_quantity)) }"
                    />
                  </div>
                </div>
              </td>
              <td class="inventory-list-item__stock">
                <div class="stock-cell-wrapper stock-available">
                  <span>{{ record.availability ?? 0 }}</span>
                  <div class="stock-icon-area">
                    <a-icon
                      v-if="getMovementIcon(record.movement_type, 'availability', record.movement_quantity)" 
                      :type="getMovementIcon(record.movement_type, 'availability', record.movement_quantity)" 
                      :style="{ color: checkColorArrow(getMovementIcon(record.movement_type, 'availability', record.movement_quantity)) }"
                    />
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <a-empty v-else class="py-5" />
      </div>
    </div>

    <div class="mt-4 text-right">
      <a-pagination
        :current="params.page"
        :page-size="params.size"
        :page-size-options="['10', '20', '30', '40', '50']"
        :total="totalRow"
        show-size-changer
        :show-total="(total, range) => $t('utils.pagination_show_total', { perpage: `${range[0]} - ${range[1]}`, total })"
        :disabled="isLoading"
        @change="onPageChange"
        @showSizeChange="onShowSizeChange"
      />
    </div>

    <ModalExportExcel
      :movement-status="movementStatusList"
      :catalog-id="catalogId"
      :visible="showModalExportExcel"
      :on-close="() => showModalExportExcel = false"
      :is-loading="loadingExport"
      @handleSubmitModal="exportDataTable"
    />
  </div>
</template>

<style lang="scss">
.pca-tab-store {
  .ant-tabs-bar {
    background-color: #fff;
    margin-bottom: 0 !important;
    border-bottom-color: transparent !important;
  }
  .ant-tabs-nav .ant-tabs-tab:hover,
  .ant-tabs-nav .ant-tabs-tab-active {
    color: #ff0a54;
  }
  .ant-tabs-ink-bar {
    background-color: #ff0a54;
  }
}
.select-antd-default.ant-select-lg .ant-select-selection__rendered {
  line-height: 38px !important;
}
</style>

<style lang="scss" scoped>
.page__movement-detail {
  .pca-tab-store::v-deep {
    .ant-tabs-large-bar .ant-tabs-nav-container {
      font-size: 14px;
    }

    .ant-tabs-large-bar .ant-tabs-tab {
      padding: 12px 14px;
    }
  }
  .loader {
    position: absolute;
    inset: 0;
    z-index: 10;
    background-color: rgba(255, 255, 255, 0.2);
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .header-detail {
    display: flex;
    align-items: center;
    margin-bottom: 24px;

    &__container {
      display: flex;
      align-items: center;
      gap: 16px;
      width: 100%;
    }

    &__title {
      font-weight: 500;
      margin: 0;
      line-height: 1;
      white-space: nowrap;
      display: flex;
      align-items: center;

      .ant-btn-link {
        color: #262626;
        padding: 0;
        height: auto;
        font-size: 18px;

        &:hover {
          color: #ff0a54;
        }
      }
    }

    &__divider {
      width: 1px;
      height: 16px;
      background: #e8e8e8;
    }

    &__product {
      font-size: 16px;
      color: #262626;
      font-weight: 500;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      min-width: 0;
    }

    .sku-copy-wrapper {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      background: #f6ffed;
      padding: 2px 10px;
      border-radius: 4px;
      cursor: pointer;
      transition: all 0.2s;
      border: 1px solid #b7eb8f;

      &:hover {
        background: #f4ffe3;
        border-color: #73d13d;
        
        .copy-icon {
          color: #52c41a;
        }
      }

      .sku-label {
        font-size: 10px;
        color: #52c41a;
        font-weight: 600;
        text-transform: uppercase;
      }

      .sku-value {
        font-family: monospace;
        font-size: 13px;
        color: #1b4e07;
        font-weight: 600;
      }

      .copy-icon {
        font-size: 12px;
        color: #b7eb8f;
      }
    }

    .warehouse-pill {
      background: #e6f7ff;
      border: 1px solid #91d5ff;
      color: #1890ff;
      border-radius: 4px;
      padding: 2px 10px;
      font-weight: 600;
      font-size: 12px;
      margin: 0;
      display: flex;
      align-items: center;
    }
  }

  .inventory-list {
    position: relative;

    &--wrapper {
      background-color: #fff;
      overflow-x: auto;
      position: relative;
      height: calc(100dvh - 333px);
      border: 1px solid #f0f2f4;

      @media screen and (max-height: 600px) {
        min-height: 500px;
      }
    }

    &.is-loading {
      overflow: hidden;
    }
  }

  .inventory-list-item__stock {
    padding: 12px 8px !important;
  }

  .stock-cell-wrapper {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    width: 100%;
    padding-right: 32px !important;
    position: relative;
  }

  .stock-icon-area {
    width: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    position: absolute;
    right: 0;
  }

  .stock-on-hand,
  .stock-reserved,
  .stock-on-order,
  .stock-available,
  .movement-quantity {
    color: #262626;
    font-weight: 500;
  }

  table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
  }

  thead {
    position: sticky;
    top: 0;
    z-index: 1;
  }

  thead th {
    z-index: 1;
    padding: 4px 12px;
    background: #f0f2f4;
    font-size: 12px;
    font-weight: 500;
    color: #757575;
    text-transform: uppercase;
    position: relative;
    vertical-align: middle;
    line-height: 1.2;
  }

  thead .stock th {
    background: #f8f9fa;
    position: relative;
    padding: 10px 16px;
    
    &:not(:last-child)::after {
      content: '';
      position: absolute;
      right: 0;
      top: 0;
      width: 1px;
      height: 100%;
      background-color: #d9d9d9;
    }
  }

  tbody tr td {
    padding: 12px 16px;
    border-bottom: 1px solid #e4e9f0;
    vertical-align: top;
  }
}
</style>
